import { useCallback, useEffect, useState } from "react";
import {
  CertificateUserRecord,
  fetchCertificateUserData,
  fetchNextCountNumber,
  sendCertificateEmail,
} from "../../../services/platformApi";
import { generateCertificatePdf } from "../../../utils/certificatePdf";
import { getCurrentDateLt } from "../../../utils/date";

interface CertificateConfigShape {
  fieldCode: string;
  productCode: string;
  hours: string;
  templatePath?: "/certificate.pdf" | "/certificatehb.pdf";
}

interface UseCertificateFlowParams {
  certificateConfig?: CertificateConfigShape;
  selectedProduct: string;
  user: unknown;
}

interface AuthUserShape {
  name?: string;
  email?: string;
}

export const useCertificateFlow = ({
  certificateConfig,
  selectedProduct,
  user,
}: UseCertificateFlowParams) => {
  const [isPdfModalOpen, setPdfModalOpen] = useState(false);
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [birthDate, setBirthDate] = useState("");
  const [fieldCode, setFieldCode] = useState("");
  const [productCode, setProductCode] = useState("");
  const [hours, setHours] = useState("");
  const [date] = useState(getCurrentDateLt());
  const [countNumber, setCountNumber] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [name, setName] = useState("");
  const [surname, setSurname] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [optionalEmail] = useState("");
  const [userData, setUserData] = useState<CertificateUserRecord | null>(null);

  const authUser = (user as AuthUserShape | undefined) ?? undefined;

  const generatePdf = useCallback(
    async (nextName: string, nextSurname: string, nextBirthDate: string) => {
      return generateCertificatePdf({
        templatePath: certificateConfig?.templatePath,
        name: nextName,
        surname: nextSurname,
        birthDate: nextBirthDate,
        productCode,
        date,
        countNumber,
        hours,
        fieldCode,
      });
    },
    [certificateConfig?.templatePath, countNumber, date, fieldCode, hours, productCode]
  );

  const handleSendCertificateEmail = useCallback(async () => {
    setSendingEmail(true);
    setEmailError(null);
    try {
      const formData = new FormData();
      formData.append("user", JSON.stringify(user));
      formData.append("name", name);
      formData.append("surname", surname);
      formData.append("selectedProduct", selectedProduct);
      formData.append("fieldCode", fieldCode);
      formData.append("birthDate", birthDate);
      formData.append("date", date);
      formData.append("productCode", productCode);
      formData.append("countNumber", countNumber);
      formData.append("hours", hours);
      formData.append("company", "temp");
      if (optionalEmail.trim()) {
        formData.append("optionalEmail", optionalEmail.trim());
      }
      formData.append("certificate", pdfBlob!, "certificate.pdf");

      await sendCertificateEmail(formData);

      setEmailSent(true);
    } catch (err: any) {
      setEmailError(err.message || "Klaida siunčiant el. laišką");
    } finally {
      setSendingEmail(false);
    }
  }, [
    birthDate,
    countNumber,
    date,
    fieldCode,
    hours,
    name,
    optionalEmail,
    pdfBlob,
    productCode,
    selectedProduct,
    surname,
    user,
  ]);

  useEffect(() => {
    if (isPdfModalOpen) {
      (async () => {
        const blob = await generatePdf(name, surname, birthDate);
        setPdfBlob(blob);
      })();
    }
  }, [birthDate, fieldCode, generatePdf, isPdfModalOpen, name, surname]);

  useEffect(() => {
    if (certificateConfig) {
      if (selectedProduct !== "hygienehb") {
        setFieldCode(certificateConfig.fieldCode);
      } else {
        setFieldCode("");
      }
      setProductCode(certificateConfig.productCode);
      setHours(certificateConfig.hours);
    }
  }, [certificateConfig, selectedProduct]);

  useEffect(() => {
    const fetchCountNumber = async () => {
      try {
        const nextCountNumber = await fetchNextCountNumber();
        if (nextCountNumber) {
          setCountNumber(nextCountNumber);
        }
      } catch (error) {
        console.error("Error fetching count number:", error);
      }
    };
    fetchCountNumber();
  }, []);

  useEffect(() => {
    const fetchUserData = async () => {
      if (!selectedProduct || !authUser?.name) return;

      try {
        const data = await fetchCertificateUserData(selectedProduct, authUser.email);

        if (!data) {
          setUserData(null);
          return;
        }

        if (data.error) {
          setUserData(null);
          console.error("User data error:", data.error);
        } else {
          setUserData(data);
        }
      } catch (error) {
        setUserData(null);
        console.error("Failed to fetch user data:", error);
      }
    };
    fetchUserData();
  }, [authUser?.email, authUser?.name, selectedProduct]);

  useEffect(() => {
    if (userData && userData.user) {
      setName(userData.user.name || "");
      setSurname(userData.user.surname || "");
      setBirthDate(userData.user.birthDate || "");
      setFieldCode(userData.user.fieldCode || "");
      setProductCode(userData.user.productCode || "");
      setCountNumber(userData.user.countNumber || "");
      setHours(userData.user.hours || "");
    }
  }, [userData]);

  return {
    agreed,
    birthDate,
    countNumber,
    date,
    emailError,
    emailSent,
    fieldCode,
    handleSendCertificateEmail,
    hours,
    isPdfModalOpen,
    name,
    optionalEmail,
    pdfBlob,
    productCode,
    sendingEmail,
    setAgreed,
    setBirthDate,
    setFieldCode,
    setName,
    setPdfModalOpen,
    setSurname,
    surname,
    userData,
  };
};
